import { useCallback, useEffect, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { parseDetail } from "../../utils/errors";
import {
  enrolSecurityKey,
  listSecurityKeys,
  removeSecurityKey,
  renameSecurityKey,
  webAuthnSupported,
  WebAuthnCancelled,
} from "../../utils/webauthn";

interface SecurityKey {
  id: number;
  nickname: string;
  created: string;
  last_used_at: string | null;
}

function formatDate(value: string | null): string {
  if (!value) {
    return "never";
  }
  return new Date(value).toLocaleDateString();
}

export default function SecurityKeys() {
  const { t } = useTranslation();

  const [keys, setKeys] = useState<SecurityKey[]>([]);
  const [nickname, setNickname] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const load = useCallback(async () => {
    const { data, status } = await listSecurityKeys();
    if (status === 200) {
      setKeys(data);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enrol = async () => {
    setError("");
    setSuccess("");
    try {
      const { data, status } = await enrolSecurityKey(nickname);
      if (status === 200) {
        setSuccess(t("settings.securityKeys.enrolled", { name: nickname }));
        setNickname("");
        return load();
      }
      setError(
        parseDetail(data?.detail, t("settings.securityKeys.enrolFailed")),
      );
    } catch (e) {
      if (e instanceof WebAuthnCancelled) {
        return;
      }
      const reason = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
      setError(`${t("settings.securityKeys.enrolFailed")} (${reason})`);
    }
  };

  const remove = async (key: SecurityKey) => {
    setError("");
    setSuccess("");
    const { data, status } = await removeSecurityKey(key.id);
    if (status === 200 || status === 204) {
      return load();
    }
    setError(parseDetail(data?.detail, t("settings.securityKeys.removeFailed")));
  };

  const startRename = (key: SecurityKey) => {
    setRenamingId(key.id);
    setRenameValue(key.nickname);
  };

  const confirmRename = async () => {
    if (renamingId === null) {
      return;
    }
    setError("");
    setSuccess("");
    const { data, status } = await renameSecurityKey(renamingId, renameValue);
    if (status === 200) {
      setRenamingId(null);
      return load();
    }
    setError(parseDetail(data?.detail, t("settings.securityKeys.renameFailed")));
  };

  return (
    <Card variant="outlined" sx={{ maxWidth: 600, mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("settings.securityKeys.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("settings.securityKeys.description")}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {!webAuthnSupported() && (
          <Alert severity="info">
            {t("settings.securityKeys.unsupported")}
          </Alert>
        )}

        {webAuthnSupported() && (
          <>
            {keys.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("settings.securityKeys.empty")}
              </Typography>
            ) : (
              <List dense sx={{ mb: 2 }}>
                {keys.map((key) => (
                  <ListItem
                    key={key.id}
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        {renamingId === key.id ? (
                          <IconButton
                            edge="end"
                            aria-label={t("settings.securityKeys.saveName")}
                            onClick={confirmRename}
                          >
                            <CheckIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            edge="end"
                            aria-label={t("settings.securityKeys.rename")}
                            onClick={() => startRename(key)}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        <IconButton
                          edge="end"
                          aria-label={t("settings.securityKeys.remove")}
                          onClick={() => remove(key)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    }
                  >
                    {renamingId === key.id ? (
                      <TextField
                        size="small"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                      />
                    ) : (
                      <ListItemText
                        primary={key.nickname}
                        secondary={t("settings.securityKeys.usage", {
                          added: formatDate(key.created),
                          used: formatDate(key.last_used_at),
                        })}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            )}

            <Stack direction="row" spacing={2}>
              <TextField
                label={t("settings.securityKeys.nicknameLabel")}
                size="small"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={enrol}
                disabled={!nickname.trim()}
              >
                {t("settings.securityKeys.enrolButton")}
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
